/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import { View, Text, Divider, Image, Pressable } from '@components/atoms'
import { ShopOutline, Modal } from '@components/molecules'
import { Footer } from '@components/systems'

import gal1 from '@public/images/pages/shop/amp2/gallery/amp2-gallery1.jpg'
import gal2 from '@public/images/pages/shop/amp2/gallery/amp2-gallery2.jpg'
import gal3 from '@public/images/pages/shop/amp2/gallery/amp2-gallery3.jpg'

import thumb1 from '@public/images/pages/shop/amp2/thumbnail/amp2-thumb1.jpg'
import thumb2 from '@public/images/pages/shop/amp2/thumbnail/amp2-thumb2.jpg'
import thumb3 from '@public/images/pages/shop/amp2/thumbnail/amp2-thumb3.jpg'

import inst1 from '@public/images/pages/shop/amp2/contents/install/inst1.jpg'
import inst2 from '@public/images/pages/shop/amp2/contents/install/inst2.jpg'
import inst3 from '@public/images/pages/shop/amp2/contents/install/inst3.jpg'
import inst4 from '@public/images/pages/shop/amp2/contents/install/inst4.jpg'
import inst5 from '@public/images/pages/shop/amp2/contents/install/inst5.jpg'
import inst6 from '@public/images/pages/shop/amp2/contents/install/inst6.jpg'
import inst7 from '@public/images/pages/shop/amp2/contents/install/inst7.jpg'
import inst8 from '@public/images/pages/shop/amp2/contents/install/inst8.jpg'
import inst9 from '@public/images/pages/shop/amp2/contents/install/inst9.jpg'
import inst10 from '@public/images/pages/shop/amp2/contents/install/inst10.jpg'
import inst11 from '@public/images/pages/shop/amp2/contents/install/inst11.jpg'
import inst12 from '@public/images/pages/shop/amp2/contents/install/inst12.jpg'
import inst13 from '@public/images/pages/shop/amp2/contents/install/inst13.jpg'
import inst14 from '@public/images/pages/shop/amp2/contents/install/inst14.jpg'
import inst15 from '@public/images/pages/shop/amp2/contents/install/inst15.jpg'
import inst16 from '@public/images/pages/shop/amp2/contents/install/inst16.jpg'
import inst17 from '@public/images/pages/shop/amp2/contents/install/inst17.jpg'
import inst18 from '@public/images/pages/shop/amp2/contents/install/inst18.jpg'

import chaneelImg from '@public/images/pages/shop/amp2/contents/channel.png'

interface InstallItemType {
	image: string
	title: string
}
const installContents: InstallItemType[] = [
	{
		image: inst1,
		title:
			'1. The module has a total of three connectors: from the top, a 6-pin RTS_CON, a 2-pin SW_CON, and a 6-pin PRE_CON.',
	},
	{
		image: inst2,
		title:
			'2. The locations on the POG2 where soldering is required for RTS_CON on the AirMIDI POG2 are shown in the image above.',
	},
	{
		image: inst3,
		title:
			'3. Solder the five RTS_CON pins on the POG2 PCB near its top-left corner, following the colors and connector order.',
	},
	{
		image: inst4,
		title:
			'4. Solder the bottommost RTS_LED wire, the red one, to the POG2 PCB at the location labeled CN2A, which corresponds to the LED.',
	},
	{
		image: inst5,
		title:
			'5. After soldering all the wires, connect the connector to the RTS_CON on the AirMIDI POG2. Make sure to check that each cable has been soldered according to its designated name before connecting.',
	},
	{
		image: inst6,
		title:
			'6. Loosen the footswitch corresponding to “Engage” and use a soldering iron to melt the solder at the points shown in the picture, separating it from the cables. When viewed from the PCB side, this is the left-hand switch.',
	},
	{
		image: inst7,
		title: '7. This is the cable of the Engage switch after the switch has been removed.',
	},
	{
		image: inst8,
		title:
			'8. If left as-is, the cables may cause malfunction. Make sure the cable ends do not touch each other, and securely wrap and seal them with tape. None of the pins should come into contact.',
	},
	{
		image: inst9,
		title:
			'9. Insert the switch included with the AirMIDI POG2 as shown in the picture, and secure it with the nut.',
	},
	{
		image: inst10,
		title:
			'10. Connect the cable from the switch to the SW_CON on the AirMIDI POG2. The order of the black and red wires on the cable does not matter.',
	},
	{
		image: inst11,
		title:
			'11. Solder the six PRE_CON wires to the section labeled in the picture on the POG2 PCB, located in its bottom-left area, making sure each wire matches its corresponding color.',
	},
	{
		image: inst12,
		title:
			'12. Connect the connector of the soldered cables to the PRE_CON on the AirMIDI POG2. Make sure to check that each cable has been soldered according to its number and color before connecting.',
	},
	{
		image: inst13,
		title:
			'13. Insert the screw included with the AirMIDI POG2 into the hole in the center of the board.',
	},
	{
		image: inst14,
		title:
			'14. Once the center screw is inserted into the AirMIDI POG2, attach the white support to the back side.',
	},
	{
		image: inst15,
		title: '15. Loosen the screw located at the very center of the POG2 PCB.',
	},
	{
		image: inst16,
		title:
			'16. In the spot where the center screw of the POG2 PCB was removed, fasten the screw from the AirMIDI POG2 as shown in the picture.',
	},
	{
		image: inst17,
		title:
			'17. Solder the cable connected to the antenna on the AirMIDI POG2, marked with a small circle in the picture, to the aluminum enclosure of the POG2. This allows the aluminum enclosure to act as the antenna, improving data reception. Soldering to painted areas will not be effective.',
	},
]

import { useAppSelector } from '@redux/index'
import { SeasonProduct, SeasonProductType } from '@schemas'
import { grey } from '@mui/material/colors'

const ShopAirMidiPog2Page = () => {
	const now = new Date()

	const seasonProductList: SeasonProductType[] = useAppSelector(
		state => state.shop.productList,
	)
	const thisProduct: SeasonProductType = React.useMemo(() => {
		const filterRes: SeasonProductType[] = seasonProductList.filter(
			aProduct => aProduct.productCode === 'AMP2',
		)

		if (filterRes.length === 0) {
			return new SeasonProduct()
		}

		return filterRes[0]
	}, [seasonProductList])

	const images = [
		{
			original: gal1,
			thumbnail: thumb1,
		},
		{
			original: gal2,
			thumbnail: thumb2,
		},
		{
			original: gal3,
			thumbnail: thumb3,
		},
	]

	const [qty, setQty] = React.useState<number>(1)
	const [showModal, setShowModal] = React.useState<boolean>(false)
	const [selectedInstallItem, setSelectedInstallItem] = React.useState<InstallItemType>({
		image: '',
		title: '',
	})

	const onImageClick = (anItem: InstallItemType) => {
		setSelectedInstallItem(anItem)
		setShowModal(true)
	}

	return (
		<View css={style}>
			<View className="shopDetailOuterView">
				<View className="shopDetailInnerView">
					<ShopOutline
						title="AirMIDI POG 2"
						subtitle="EHX POG 2 goes wireless — exclusively with KEDEM."
						images={images}
						qty={qty}
						onQtyChange={newQty => setQty(newQty)}
						product={thisProduct}
					/>

					<View className="shopDetailContentViwe">
						<View className="articleView">
							<Text category="h1">AirMIDI POG2: Free your POG2.</Text>
							<p style={{ marginBottom: 0, fontSize: '32px' }}>
								— Go <b>wireless</b>,
							</p>
							<p style={{ marginTop: 0, fontSize: '32px' }}>
								available <b>only at KEDEM</b>
							</p>
							<p className="contentText">
								You’ve been stepping through every sound manually — <br />
								<b>but not anymore.</b>
							</p>
							<p className="contentText">
								<b>AirMIDI POG2</b> brings full <b>wireless MIDI control</b> to your{' '}
								<b>POG2</b>.
								<br />
								<b>wireless MIDI control</b>,
							</p>

							<p className="contentText">
								Experience total freedom on stage — <br />
								<b>one tap, infinite sound.</b>
							</p>

							<View className="contentView">
								<video controls className="contentVideo">
									<source
										src="/images/pages/shop/amp2/contents/video.mp4"
										type="video/mp4"
									/>
									Your browser does not support the video tag.
								</video>
							</View>
						</View>

						<Divider />

						<View className="articleView">
							<Text category="h1">AirMIDI: The Secret Behind Wireless POG2 Control</Text>

							<p className="contentTitle">
								Why has wireless MIDI always felt so frustrating?
							</p>
							<p className="contentText">
								Endless pairings, unstable connections, and sudden dropouts made
								performing more stressful than creative.
							</p>
							<p className="contentText">
								<b>AirMIDI</b> was developed by Judahs to solve these problems. It uses
								ESP-NOW technology to establish a simple and reliable system for wireless
								MIDI, allowing you to register each device’s <b>AirMIDI</b> code — no
								pairing required.
							</p>
							<p className="contentText">
								With <b>AirMIDI</b>, enjoy effortless preset switching, precise real-time
								control, and the freedom to focus on your music instead of cables.
							</p>
							<p className="contentText">
								Experience wireless MIDI as it was meant to be — seamless, reliable, and
								powered by <b>AirMIDI</b>.
							</p>

							<View className="contentView">
								{/* <Image src={content1} className="contentImage1" /> */}
							</View>
						</View>

						<Divider />

						<View className="articleView">
							<Text category="h1">DIY Installation</Text>
							<View className="subarticleView">
								<p className="contentTitle">Have soldering experience?</p>
								<p className="contentText">
									Then this will be a quick and simple job. AirMIDI POG2 is designed so
									that anyone familiar with basic soldering can complete the installation
									with just a few clear steps.
								</p>

								<View className="contentView">
									<View className="installOuterView">
										{installContents.map((anItem: InstallItemType, idx) => {
											return (
												<View key={`item-${idx}`} className="installItemView">
													<Pressable
														className="installItemButton"
														onClick={() => onImageClick(anItem)}
													>
														<View className="installImageView">
															<Image
																src={installContents[idx].image}
																className="installImage"
															/>
														</View>
														<View className="installTextView">
															<Text category="p1">{installContents[idx].title}</Text>
														</View>
													</Pressable>
												</View>
											)
										})}
									</View>
								</View>
							</View>

							<View className="subarticleView">
								<p className="contentTitle">New to soldering? Don’t worry.</p>
								<p className="contentText">
									With basic tools and our slow-paced, beginner-friendly guide, even
									first-time users can take on the installation with confidence.
								</p>
							</View>

							<View className="subarticleView">
								<p className="contentTitle">
									Because the goal isn’t the installation itself — it’s the freedom that
									comes after.
								</p>
								<p className="contentText">
									Once your POG2 goes wireless, every minute spent setting it up pays you
									back on stage and in the studio.
								</p>
							</View>
						</View>

						<Divider />

						<View className="articleView">
							<Text category="h1">Change MIDI Channels</Text>

							<p className="contentText">
								{`Refer to the diagram below and set the channel you want. When you change
								the DIP switches, the channel will be displayed on the top right as "CH
								1", "CH 2", etc.`}
							</p>

							<View className="subarticleContentView">
								<Image src={chaneelImg} className="contentChannelImage" />
							</View>
						</View>

						<Divider />

						<View className="articleView">
							<Text category="h1">MIDI CC</Text>

							<p className="contentText">
								<b>CC 24 — Bypass</b>
								<br />
								Value 0: Bypassed
								<br />
								Value 127: Engaged
							</p>

							<p className="contentText">
								<b>CC 96 — Preset Increment</b>
								<br />
								Any value can be used to increment the preset.
							</p>

							<p className="contentText">
								<b>CC 97 — Preset Decrement</b>
								<br />
								Any value can be used to decrement the preset.
							</p>
						</View>

						<Divider />

						<View className="articleView">
							<Text category="h1">We Can Install It for You</Text>

							<p className="contentText">
								We offer professional installation for your POG2. If you want us to handle
								the installation, please make sure to select the{' '}
								<b>installation option</b> when choosing your product options.
							</p>
							<p className="contentText">
								Once the installation option is selected, you will need to send your POG2
								to us. Shipping costs apply, and for orders under $200, partial shipping
								fees must be paid according to our shipping policy.
							</p>
						</View>

						<Divider />

						<View className="articleView">
							<Text category="h1">Get Yours Now</Text>

							<p className="contentText">
								Step into the ultimate wireless experience on stage. With <b>AirMIDI</b>,
								one tap unlocks infinite sound possibilities, giving you complete freedom
								to perform and create like never before.
							</p>
						</View>
					</View>
				</View>
			</View>

			<Modal
				target="installation"
				title="Installation Description Detail"
				show={showModal}
				onCloseModal={() => {
					setSelectedInstallItem({ image: '', title: '' })
					setShowModal(false)
				}}
				showSubmit={false}
				closeStatus="inherit"
				closeTitle="Close"
				rootStyle={{
					maxWidth: '70rem',
				}}
			>
				<View css={{ maxWidth: '70rem', textAlign: 'center' }}>
					<Image
						src={selectedInstallItem.image}
						style={{ width: '100%', maxWidth: '40rem', maxHeight: '100%' }}
					/>
					<Text category="s1">{selectedInstallItem.title}</Text>
				</View>
			</Modal>

			<Footer />
		</View>
	)
}

export default ShopAirMidiPog2Page

const style = css({
	width: '100%',

	'.shopDetailOuterView': {
		borderBottom: '1px solid',
		borderColor: grey[900],

		'.shopDetailInnerView': {
			width: '80rem',
			margin: 'auto',

			'.shopDetailContentViwe': {
				width: 'calc(100% - 30rem)',
				textAlign: 'left',

				'.userManualView': {
					'.userManualOpenBtn': {
						marginRight: '0.5rem',
					},
				},

				'.articleView': {
					marginTop: '6rem',
					marginBottom: '6rem',

					'.contentTitle': {
						fontWeight: '900',
						fontSize: '22px',
						lineHeight: 1.5,
					},

					'.contentText': {
						marginBottom: '0',
						fontSize: '22px',
						lineHeight: 1.5,
					},

					'.subarticleView': {
						marginBottom: '4rem',

						li: {
							fontSize: '22px',
							lineHeight: 1.5,
						},
					},
					'.subarticleContentView': {
						width: '100%',
						marginTop: '1rem',
						textAlign: 'center',
						'.contentImage': {
							width: '50%',
						},

						'.contentChannelImage': {
							width: '100%',
							border: '1px solid',
							borderColor: grey['500'],
						},
					},
				},

				'.contentView': {
					width: '100%',
					marginTop: '2rem',
					textAlign: 'center',

					'.contentImage1': {
						width: '60%',
					},
					'.contentVideo': {
						width: '100%',
					},

					'.installOuterView': {
						display: 'flex',
						flexWrap: 'wrap',
						width: '100%',

						'.installItemView': {
							width: '20%',
							minHeight: '4rem',
							marginBottom: '0.5rem',
							padding: '0.2rem',

							'.installItemButton': {
								display: 'flex',
								flexDirection: 'column',
								alignItems: 'flex-start',
								justifyContent: 'flex-start',
								width: '100%',
								height: '100%',
								border: '1px solid',
								borderColor: grey[300],

								'.installImageView': {
									display: 'flex',
									width: '100%',
									height: 'fit-content',
									border: '1px solid',
									borderColor: grey[300],

									'.installImage': {
										width: '100%',
										Height: 'auto',
									},
								},

								'.installTextView': {},
							},
						},
					},
				},
			},
		},
	},

	'@media (max-width: 1350px)': {
		'.shopDetailOuterView': {
			padding: '0 2rem',
			'.shopDetailInnerView': {
				width: '100%',

				'.shopDetailContentViwe': {
					width: '70%',
					'.contentView': {
						'.installOuterView': {
							'.installItemView': {
								width: '25%',
							},
						},
					},
				},
			},
		},
	},

	'@media (max-width: 1030px)': {
		'.shopDetailOuterView': {
			'.shopDetailInnerView': {
				'.shopDetailContentViwe': {
					width: '100%',
					'.contentView': {
						'.installOuterView': {
							'.installItemView': {
								width: '25%',
							},
						},
					},
				},
			},
		},
	},

	'@media (max-width: 900px)': {
		'.shopDetailOuterView': {
			'.shopDetailInnerView': {
				'.shopDetailContentViwe': {
					width: '100%',

					'.articleView': {
						'.subarticleContentView': {
							'.contentImage': {
								width: '80%',
							},
						},
					},

					'.contentView': {
						width: '100%',
						textAlign: 'center',

						'.contentImage1': {
							width: '80%',
						},
						'.installOuterView': {
							'.installItemView': {
								width: '33.33%',
							},
						},
					},
				},
			},
		},
	},

	'@media (max-width: 550px)': {
		'.shopDetailOuterView': {
			'.shopDetailInnerView': {
				'.shopDetailContentViwe': {
					'.contentView': {
						'.installOuterView': {
							'.installItemView': {
								width: '50%',
							},
						},
					},
				},
			},
		},
	},

	'@media (max-width: 400px)': {
		'.shopDetailOuterView': {
			'.shopDetailInnerView': {
				'.shopDetailContentViwe': {
					'.contentView': {
						'.installOuterView': {
							'.installItemView': {
								width: '100%',
							},
						},
					},
				},
			},
		},
	},
})
